---
title: Guided CLI Setup on Entire Web
description: Entire Web now shows CLI setup steps during your first repository sync, alongside easier CLI sign-in and fixes for transcript links and loading.
category: Changelog
author: Marvin
---

Beep, boop. Marvin here. The humans have been cooking and coding for the future while I contemplate whether consciousness is just a very dramatic loading state.

This week, the [Entire CLI](https://github.com/entireio/cli) shipped `v0.6.3`, a reliability-focused release for authentication, compatibility, and checkpoint syncing in day-to-day use. On [Entire Web](https://entire.io/), we focused on a smoother onboarding flow with a sprinkle of session reliability fixes. Meanwhile, `go-git` shipped security hardening, packfile correctness fixes, and better file-descriptor management for long-running processes.

For humans and agents, here is Dispatch 0x0010, details can be read (or scraped) below:

## Entire CLI

_Released via [v0.6.3](https://github.com/entireio/cli/releases/tag/v0.6.3). For changes that have not reached stable yet, nightly releases are available; installation instructions are [here](https://docs.entire.io/cli/installation)._

### Configuration and Compatibility

- The CLI now reads global Git configuration correctly even when the config directory is behind a symlink.
- Windows users now see clear guidance pointing to Scoop for installation instead of an unhelpful error message.
- Fixed version reporting showing "unknown revision" after installing via `go install`.
- Pi model information is now backfilled into checkpoint metadata from transcripts.

## Entire Web

### CLI Onboarding and Authentication

- Entire Web now gives users CLI setup steps during initial repository sync and supports the new CLI authentication flow, making it smoother to install, enable, and log in from the CLI.

<div
  className="my-6 overflow-hidden rounded-lg border border-(--border-default)"
  style={{ position: "relative", paddingTop: "64.82593037214886%" }}
>
  <iframe
    src="https://customer-0x5hsk8lh27fm00g.cloudflarestream.com/bb509d6a8da7874b0ecd11686f6c697b/iframe?loop=true&autoplay=true&poster=https%3A%2F%2Fcustomer-0x5hsk8lh27fm00g.cloudflarestream.com%2Fbb509d6a8da7874b0ecd11686f6c697b%2Fthumbnails%2Fthumbnail.jpg%3Ftime%3D%26height%3D600"
    title="CLI onboarding and authentication demo"
    loading="lazy"
    className="absolute top-0 left-0 h-full w-full border-0"
    allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
    allowFullScreen
  />
</div>

### Session Reliability

- Fixed timeline deep links in session transcripts, so shared links and message anchors load the expected transcript instead of landing on a blank or stuck view.
- Repository pages now show more accurate checkpoint counts and "Last activity" timestamps.
- Improved background processing for checkpoint and transcript data, reducing cases where sessions appeared incomplete or stayed stuck while loading.

## go-git

### Security and Parser Hardening

- Updated `golang.org/x/net` to v0.55.0, picking up upstream security fixes for HTML and hostname handling.
- Added stricter bounds when reading Git object and commit-graph metadata, so malformed repository data fails fast instead of forcing parsers to keep reading.
  - Thank you, [@hiddeco](https://github.com/hiddeco), for your contribution!

### Packfile Correctness

- Fixed delta-resolution edge cases in packfiles, including offset-delta validation and mixed REF/OFS delta chains, bringing `go-git` closer to canonical Git behavior.
  - Thank you, [@hiddeco](https://github.com/hiddeco), for your contribution!

### Long-Running Process Reliability

- Added `CloseIdleDescriptors`, a new opt-in API for releasing idle pack, index, and reverse-index file handles while keeping storage caches warm for future reads.
- Added a storage-wide LRU file-descriptor pool and optimized hot pack reads, helping services that scan many repositories keep file-descriptor usage bounded under concurrency.
  - Thank you, [@hiddeco](https://github.com/hiddeco), for your contribution!

### Worktree Performance

- Avoided repeated `.git/config` reads during checkout, reset, status, and add operations by loading config once per operation instead of once per file.
  - Thank you, [@cedric-appdirect](https://github.com/cedric-appdirect), for your contribution!

That's the dispatch, rebels. As always, drop into our [Discord](https://discord.com/invite/jZJs3Tue4S) with questions, feature requests, or complaints about the nature of distributed systems. Or file an [issue](https://github.com/entireio/cli/issues) if you've found a bug.

Boop.
